import type { NextPage } from 'next'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { Page, PageBackgroundColor } from 'src/components/layouts/Page'
import { BlogPostBackgroundColor } from 'src/components/molecules/BlogPosts/types'
import { Content } from 'src/components/molecules/sanity/Content'
import { NewsAndStoriesSection } from 'src/components/organisms/Home'

import { NotFound } from 'src/pages/404/404'
import { formatDate } from 'src/utils/date'
import { getSanityImageUrl } from 'src/utils/sanity'
import { trpc } from 'src/utils/trpc'

import { PostHero } from './PostHero'
import { PostNavigation } from './PostNavigation'

const Post: NextPage = () => {
  const router = useRouter()

  const { slug: slugParts } = router.query as { slug: string[] }
  const slug = [...slugParts].reduce(
    (acc, part, index) => (index ? `${acc}/${part}` : part),
    ''
  )
  const { data: post, isLoading } = trpc.useQuery([
    'sanity.getPostBySlug',
    { slug }
  ])

  useEffect(() => {
    if (post && post.slug.current !== slug) {
      router.replace({ pathname: post?.slug.current })
    }
  })

  // Redirect if page not found
  if (!post) {
    return isLoading ? null : <NotFound />
  }

  const {
    body,
    description,
    lastPost,
    mainImage,
    nextPost,
    publishedAt,
    summary,
    title,
    estimatedReadingTime
  } = post

  const date = formatDate({
    date: publishedAt,
    format: 'DD MMM YYYY',
    isUtc: true
  })

  return (
    <Page
      title={title}
      metaTitle={title}
      metaDescription={description}
      image={getSanityImageUrl(mainImage, 1200, 630)}
      isFullWidth
      showBreadcrumbs={false}
      backgroundColor={PageBackgroundColor.white}
    >
      <>
        <PostHero
          title={title}
          postDate={date}
          imageUrl={mainImage}
          readingTime={`${estimatedReadingTime} mins read`}
          summary={summary}
        />
        <article className='container lg:!max-w-[800px] px-5 md:px-0 mb-8 mx-auto mt-10 '>
          <Content body={body} />
          <PostNavigation
            lastPostSlug={lastPost?.slug?.current}
            nextPostSlug={nextPost?.slug?.current}
          />
        </article>
        <NewsAndStoriesSection
          title='More news and stories'
          backgroundColor={BlogPostBackgroundColor.linen}
          featuredOn='blogPage'
        />
      </>
    </Page>
  )
}

export default Post
